import React, { useEffect, useState } from 'react';
import useLiveCalls from './useLiveCalls';
import ActiveCallDrawer from './ActiveCallDrawer';
import OutboundCallComposer from './OutboundCallComposer';
import './LiveVoicePanel.css';

function maskPhone(value) {
  if (!value) return '—';
  const digits = String(value).replace(/[^0-9+]/g, '');
  if (digits.length <= 4) return digits;
  return `${digits.slice(0, 4)}…${digits.slice(-4)}`;
}

function statusLabel(status) {
  switch (status) {
    case 'inbound_received': return 'Ringing';
    case 'accepted': return 'AI active';
    case 'authenticating': return 'In auth';
    case 'authenticated': return 'Authed';
    case 'transferring': return 'Transferring';
    case 'rejected_pilot': return 'Rejected';
    case 'ended': return 'Ended';
    case 'outbound_queued': return 'Queued';
    case 'outbound_dialing': return 'Dialing';
    case 'outbound_ringing': return 'Ringing';
    case 'outbound_in_progress': return 'Connected';
    case 'outbound_completed': return 'Completed';
    case 'outbound_failed': return 'Outbound failed';
    default: return status || '—';
  }
}

function statusClass(status) {
  switch (status) {
    case 'authenticated': return 'live-status live-status-ok';
    case 'authenticating': case 'inbound_received': case 'accepted':
    case 'outbound_dialing': case 'outbound_ringing': case 'outbound_in_progress':
      return 'live-status live-status-active';
    case 'outbound_queued': return 'live-status live-status-queued';
    case 'transferring': return 'live-status live-status-transfer';
    case 'rejected_pilot': case 'ended': case 'outbound_failed': case 'outbound_completed':
      return 'live-status live-status-ended';
    default: return 'live-status live-status-unknown';
  }
}

function directionLabel(direction) {
  return direction === 'outbound' ? 'OUT' : 'IN';
}

function elapsedSince(iso) {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return '—';
  const seconds = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h`;
}

export default function LiveVoicePanel() {
  const [selectedCallId, setSelectedCallId] = useState(null);
  const { sessions, active, inAuth, transferring, loading, error, refetch } = useLiveCalls({ limit: 25 });

  // Tick once a second so the elapsed-time column on each row stays current.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (sessions.length === 0) return undefined;
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [sessions.length]);

  function handleSelectCall(callId) {
    setSelectedCallId(callId);
  }

  return (
    <div className="live-voice-panel">
      <header className="live-voice-header">
        <div className="live-voice-header-titles">
          <h1 className="live-voice-title">Voice — Live</h1>
          <p className="live-voice-subtitle">
            Active inbound calls with realtime transcript and operator controls.
          </p>
        </div>
        <div className="live-voice-header-actions">
          <button
            type="button"
            className="live-voice-refresh"
            onClick={() => refetch()}
            disabled={loading}
            title={loading ? 'Refreshing…' : 'Refresh active calls'}
            aria-label={loading ? 'Refreshing active calls' : 'Refresh active calls'}
          >
            <svg viewBox="0 0 16 16" width="14" height="14" fill="currentColor" aria-hidden="true" focusable="false">
              <path d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.418A6 6 0 1 1 8 2v1z" />
              <path d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966a.25.25 0 0 1 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466z" />
            </svg>
          </button>
          <OutboundCallComposer onCallQueued={() => refetch()} />
        </div>
      </header>

      <div className="live-voice-kpis" role="region" aria-label="Voice KPIs">
        <div className={`live-voice-kpi ${active.length > 0 ? 'active' : ''}`}>
          <span className="live-voice-kpi-value">{active.length}</span>
          <span className="live-voice-kpi-label">Active calls</span>
        </div>
        <div className={`live-voice-kpi ${inAuth.length > 0 ? 'in-auth' : ''}`}>
          <span className="live-voice-kpi-value">{inAuth.length}</span>
          <span className="live-voice-kpi-label">In auth</span>
        </div>
        <div className={`live-voice-kpi ${transferring.length > 0 ? 'transferring' : ''}`}>
          <span className="live-voice-kpi-value">{transferring.length}</span>
          <span className="live-voice-kpi-label">Transferring</span>
        </div>
      </div>

      <div className="live-voice-body">
        <section className="live-voice-list" aria-label="Active calls">
          {error ? (
            <div className="live-voice-error" role="alert">
              <p>Couldn't load active calls.</p>
              <p className="live-voice-error-detail">{String(error.message || error)}</p>
              <button type="button" onClick={() => refetch()}>Retry</button>
            </div>
          ) : loading && sessions.length === 0 ? (
            <div className="live-voice-loading" aria-busy="true">
              <div className="live-voice-skeleton-row" />
              <div className="live-voice-skeleton-row" />
              <div className="live-voice-skeleton-row" />
            </div>
          ) : active.length === 0 ? (
            <div className="live-voice-empty">
              <div className="live-voice-empty-icon" aria-hidden="true">
                <svg viewBox="0 0 32 32" width="40" height="40" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 14a7 7 0 0 1 14 0v2a3 3 0 0 1-3 3h-1.5a3 3 0 0 1-3 2.5h-2a1 1 0 1 1 0-2h2c.45 0 .82-.31.93-.73l.07-.27h-1.5a3 3 0 0 1-3-3v-1.5z" />
                  <path d="M16 5v3" />
                  <circle cx="16" cy="3" r="1.2" fill="currentColor" stroke="none" />
                </svg>
              </div>
              <h3>No active calls</h3>
              <p>Inbound sessions appear here as they arrive.</p>
              <p className="live-voice-empty-tip">Use <strong>Place call</strong> in the header to queue an outbound.</p>
            </div>
          ) : (
            <ul className="live-voice-rows">
              {active.map((session) => (
                <li key={session.callId}>
                  <button
                    type="button"
                    className={`live-voice-row ${selectedCallId === session.callId ? 'selected' : ''}`}
                    onClick={() => handleSelectCall(session.callId)}
                    aria-pressed={selectedCallId === session.callId}
                  >
                    <span className={`live-voice-row-direction live-voice-row-direction-${session.direction || 'inbound'}`}>
                      {directionLabel(session.direction)}
                    </span>
                    <div className="live-voice-row-numbers">
                      <span className="live-voice-row-from">{maskPhone(session.fromNumber)}</span>
                      <span className="live-voice-row-arrow" aria-hidden="true">→</span>
                      <span className="live-voice-row-to">{maskPhone(session.toNumber)}</span>
                    </div>
                    <span
                      className={statusClass(session.status)}
                      aria-label={`Status: ${statusLabel(session.status)}`}
                    >
                      <span className="live-status-dot" aria-hidden="true" />
                      {statusLabel(session.status)}
                    </span>
                    <span className="live-voice-row-age" aria-label={`Started ${elapsedSince(session.createdAt)} ago`}>
                      {elapsedSince(session.createdAt)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
        <section className="live-voice-drawer" aria-label="Selected call detail">
          <ActiveCallDrawer
            callId={selectedCallId}
            onCleared={() => setSelectedCallId(null)}
          />
        </section>
      </div>
    </div>
  );
}
